/*
  customsirge_home_benefits_bands.js
  Purpose:  rAF-throttled scroll-progress driver for the pinned stacked-card
            sequence (decision 0056 §1, v1.1 B-06 rev2 — replaces rejected
            rev1, commit 70b5107). Writes ONE CSS custom property,
            --customsirge-bands-progress (0..transitions, fractional), read
            by assets/customsirge_home_benefits_bands.css to drive every
            card's translateY entrance and covered-card opacity dim via
            calc()/clamp() — same "JS writes one number, CSS does the rest"
            split as customsirge_pdp_main.js's initGallerySwap (.tiles-pin /
            B-22 rev3, commit 47295ef), which this file's wrapper + spacer +
            sticky-stage mechanism is modelled on one level up (the whole
            section, not one row of tiles).
  Spec:     .claude/knowledge/decisions/0056-homepage-motion-and-awards-v1.1.md
            §1 (REVISED). No wheel/touch hijack — native scroll only, this
            file only READS scroll position via getBoundingClientRect.
  Data:     none ambient — reads only the section root's own DOM
            (.__stage-wrap / .__body / .__bands) and the
            --customsirge-hbb-transitions custom property the section file
            sets inline (band_count - 1). Registers ONE component,
            'home_benefits_bands' (docs/CONVENTIONS.md #4 — JS registers,
            never auto-runs).
  Owner:    figma-fixer(home_benefits_bands) v1.1 B-06 rev2, 2026-09-15.
  Updated:  figma-fixer(home_benefits_bands), 2026-09-28 — desktop blank
            tail: also writes --customsirge-hbb-tail (px), the part of the
            released stage below the last card's copy/stickers + the drawn
            40px band-4 bottom padding; the CSS pulls the next section up
            over exactly that much (see measureTail below).
  Updated:  figma-fixer(home_benefits_bands), 2026-09-28 — (a) the stage now
            pins at top = --customsirge-header-h, so progress is measured from
            the stage's computed `top`, not from 0; (b) fitDecorations writes
            --hbb-fit / --hbb-fit-top per sticker/photo so the art clears the
            copy by >= 24px and fits its card (CSS multiplies, see the
            stylesheet's 2026-09-28 decorations rule).
*/
(function () {
  'use strict';

  function initScrollStack(root) {
    var wrap = root.querySelector('.customsirge-home-benefits-bands__stage-wrap');
    var stage = root.querySelector('.customsirge-home-benefits-bands__body');
    var deck = root.querySelector('.customsirge-home-benefits-bands__bands');
    if (!wrap || !stage || !deck) return undefined;

    var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;

    function active() {
      if (!root.classList.contains('customsirge-home-benefits-bands--scroll-stack')) return false;
      if (reduceQuery && reduceQuery.matches) return false;
      if (document.documentElement.classList.contains('customsirge-motion-paused')) return false;
      return true;
    }

    var cards = deck.querySelectorAll('.customsirge-home-benefits-bands__band');
    var head = stage.querySelector('.customsirge-home-benefits-bands__head');
    var mobileLayout = null;
    var mobileViewport = null;
    function clearMobile() {
      root.classList.remove('customsirge-hbb-mobile-ready');
      ['--hbb-mobile-card-h', '--hbb-mobile-stage-h', '--hbb-mobile-wrap-h', '--hbb-mobile-pin-y'].forEach(function (name) {
        root.style.removeProperty(name);
      });
      for (var i = 0; i < cards.length; i++) cards[i].style.removeProperty('--hbb-mobile-y');
      mobileLayout = null;
    }
    function fitMobile(on) {
      if (!on) { clearMobile(); mobileViewport = null; return; }
      // Activate the compact stage only after its controller is running.
      // A failed/disabled script leaves readable cards with no empty scroll runway.
      root.classList.add('customsirge-hbb-mobile-ready');
      var width = document.documentElement.clientWidth || window.innerWidth;
      if (!mobileViewport || mobileViewport.width !== width) {
        mobileViewport = { width: width, height: window.innerHeight || document.documentElement.clientHeight };
      }
      // Freeze viewport height through mobile toolbar expansion/collapse;
      // orientation/width changes establish a new measurement.
      var viewportHeight = mobileViewport.height;
      var copyHeight = 0;
      for (var i = 0; i < cards.length; i++) {
        var copy = cards[i].querySelector('.customsirge-home-benefits-bands__band-content');
        if (copy) copyHeight = Math.max(copyHeight, copy.offsetHeight);
      }
      var cardHeight = Math.max(300, Math.ceil(copyHeight + 160));
      var transitions = Math.max(0, cards.length - 1);
      var headerHeight = parseFloat(getComputedStyle(root).getPropertyValue('--customsirge-header-h')) || 64;
      var minimum = (head ? head.offsetHeight : 0) + cardHeight + transitions * 18 + 20;
      var stageHeight = Math.ceil(Math.max(viewportHeight - headerHeight, minimum));
      var budget = transitions * viewportHeight * 0.7;
      mobileLayout = {
        transitions: transitions,
        budget: budget,
        pinTop: Math.min(headerHeight, viewportHeight - stageHeight),
        step: cardHeight - 32 - 18
      };
      root.style.setProperty('--hbb-mobile-card-h', cardHeight + 'px');
      root.style.setProperty('--hbb-mobile-stage-h', stageHeight + 'px');
      root.style.setProperty('--hbb-mobile-wrap-h', (stageHeight + budget) + 'px');
    }

    function updateMobile() {
      if (!mobileLayout) return;
      var y = Math.max(0, Math.min(mobileLayout.budget, mobileLayout.pinTop - wrap.getBoundingClientRect().top));
      var progress = mobileLayout.budget ? y / mobileLayout.budget * mobileLayout.transitions : 0;
      // One compact stage is held in place; each wave panel opens inside it.
      // Pixel transforms avoid mobile sticky/overflow and CSS math differences.
      root.style.setProperty('--hbb-mobile-pin-y', y.toFixed(2) + 'px');
      root.style.setProperty('--customsirge-bands-progress', String(progress));
      for (var i = 0; i < cards.length; i++) {
        var offset = i * 18 + Math.max(0, i - progress) * mobileLayout.step;
        cards[i].style.setProperty('--hbb-mobile-y', offset.toFixed(2) + 'px');
      }
    }

    var ticking = false;
    var disposed = false;
    var frame = 0;
    var listening = false;
    var lastTail = '';

    // customsirge: 2026-09-28 — the stage is a fixed viewport-driven height
    // (75dvh, floored by .__body's 860px min-height) while every card's copy
    // and stickers keep their drawn px anchors, so once the sequence releases
    // everything between the last card's copy and the stage's bottom is
    // blank card colour (317px at 1512x890). This measures that slack from
    // the last card's RESTING layout (offsetTop chain — transforms and the
    // entrance translateY never enter it, so the value is the same at any
    // scroll position) and keeps the drawn 40px band-4 bottom padding
    // (spec.json spacing.desktop: band4 li bottom 1622 vs frame 1662).
    var TAIL_PAD = 40;
    function measureTail() {
      var cards = deck.querySelectorAll('.customsirge-home-benefits-bands__band');
      var last = cards[cards.length - 1];
      if (!last) return 0;
      var bottom = 0;
      var parts = last.querySelectorAll(
        '.customsirge-home-benefits-bands__band-content, .customsirge-home-benefits-bands__sticker, .customsirge-home-benefits-bands__photo'
      );
      for (var i = 0; i < parts.length; i++) {
        var el = parts[i];
        if (!el.offsetHeight) continue;
        var b = el.offsetTop + el.offsetHeight;
        if (b > bottom) bottom = b;
      }
      var tail = stage.offsetHeight - (deck.offsetTop + last.offsetTop + bottom + TAIL_PAD);
      // customsirge: 2026-09-29 — never less than the next band's top-wave
      // depth. The pull-up is what puts band 4 under the quiz's wave dips; a
      // decoration reaching the card's bottom (band 4's photo) drove it to 0 and
      // the page background showed in the dips as a white wave.
      var MIN_TAIL = 40;
      return tail > MIN_TAIL ? Math.floor(tail) : MIN_TAIL;
    }

    // customsirge: 2026-09-28 — decorations vs copy (ADA + cropping). Each
    // sticker/photo carries its drawn 1440 box inline (--hbb-x/-y/-w/-h, and
    // --hbb-r for a right-anchored photo). Scaling that box by s about its
    // anchored viewport edge keeps its drawn bleed; s is the largest value
    // <= 1 that (1) leaves CLEAR px between the art and the card's copy box
    // and (2) fits the card's height. The top is the drawn top times s,
    // clamped inside the deck (not above .__bands, which clips) and above the
    // stage bottom. All reads are offset* layout (transforms ignored), so the
    // result does not depend on scroll position.
    var CLEAR = 24;
    var FLOAT = 8; // the float keyframe lifts a sticker 8px
    function px(el, name) {
      return parseFloat(el.style.getPropertyValue(name)) || 0;
    }
    function clearFit(el) {
      el.style.removeProperty('--hbb-fit');
      el.style.removeProperty('--hbb-fit-top');
    }
    function fitDecorations(on) {
      var cards = deck.querySelectorAll('.customsirge-home-benefits-bands__band');
      for (var c = 0; c < cards.length; c++) {
        var card = cards[c];
        var decos = card.querySelectorAll('.customsirge-home-benefits-bands__sticker, .customsirge-home-benefits-bands__photo');
        if (!on) {
          for (var d0 = 0; d0 < decos.length; d0++) clearFit(decos[d0]);
          continue;
        }
        // 2026-10-06: optional cap on the art's height as a share of the card
        // (CSS --customsirge-hbb-deco-max, e.g. 0.8; unset = no extra cap).
        var decoMax = parseFloat(getComputedStyle(card).getPropertyValue('--customsirge-hbb-deco-max')) || 0;
        var copy = card.querySelector('.customsirge-home-benefits-bands__band-content');
        var W = card.offsetWidth;
        var H = card.offsetHeight;
        var above = card.offsetTop; // room above the card inside .__bands
        var copyL = copy ? copy.offsetLeft : W / 2;
        var copyR = copy ? copy.offsetLeft + copy.offsetWidth : W / 2;
        for (var d = 0; d < decos.length; d++) {
          var el = decos[d];
          var w = px(el, '--hbb-w');
          var h = px(el, '--hbb-h');
          if (!w || !h) continue;
          var y = px(el, '--hbb-y');
          var isRight = el.classList.contains('customsirge-home-benefits-bands__sticker--right');
          var isPhotoRight = el.classList.contains('customsirge-home-benefits-bands__photo--anchor-right');
          var edge = isPhotoRight ? px(el, '--hbb-r') : px(el, '--hbb-x');
          // horizontal: s * (edgeOffset + w) <= room between that viewport edge and the copy
          var room = isRight || isPhotoRight ? W - copyR - CLEAR : copyL - CLEAR;
          var span = edge + w;
          var s = span > 0 ? room / span : 1;
          // vertical: the whole box fits between the deck top and the stage bottom
          var fitH = (H + above - FLOAT) / h;
          if (fitH < s) s = fitH;
          if (decoMax > 0 && (decoMax * H) / h < s) s = (decoMax * H) / h;
          if (s > 1) s = 1;
          if (s < 0) s = 0;
          var top = y * s;
          // 2026-10-09: --customsirge-hbb-peek lets the art rise above the card
          var peek = parseFloat(getComputedStyle(card).getPropertyValue('--customsirge-hbb-peek')) || 0;
          var minTop = -above - peek + FLOAT;
          var maxTop = H - h * s;
          if (top > maxTop) top = maxTop;
          if (top < minTop) top = minTop;
          el.style.setProperty('--hbb-fit', s.toFixed(4));
          el.style.setProperty('--hbb-fit-top', top.toFixed(1) + 'px');
        }
      }
    }
    var fitted = null;
    function refit() {
      var on = active();
      var mobile = desktopQuery && !desktopQuery.matches;
      fitDecorations(on && !mobile);
      // 2026-10-19: the mobile pinned stage is off. Its cards covered each
      // other mid-copy while sliding; without it the cards use the compact
      // normal-flow layout in the CSS (full copy, no scroll runway).
      fitMobile(false);
      fitted = on;
    }

    function setTail(v) {
      var str = v + 'px';
      if (str === lastTail) return;
      lastTail = str;
      root.style.setProperty('--customsirge-hbb-tail', str);
    }

    function update() {
      ticking = false;
      frame = 0;
      if (disposed) return;
      if (!active()) {
        clearMobile();
        root.style.setProperty('--customsirge-bands-progress', '0');
        setTail(0);
        if (fitted !== false) refit();
        return;
      }
      if (fitted !== true) refit();
      if (desktopQuery && !desktopQuery.matches) {
        setTail(0);
        updateMobile();
        return;
      }
      // Both breakpoints use this same progress driver. Only their panel
      // geometry and scroll pacing differ; phones no longer scroll tall cards.
      setTail(desktopQuery && !desktopQuery.matches ? 0 : measureTail());
      var transitions = parseFloat(getComputedStyle(wrap).getPropertyValue('--customsirge-hbb-transitions')) || 0;
      var stageHeight = stage.getBoundingClientRect().height || 0;
      var wrapRect = wrap.getBoundingClientRect();
      var budgetTotal = wrapRect.height - stageHeight;
      if (transitions <= 0 || budgetTotal <= 0) {
        root.style.setProperty('--customsirge-bands-progress', '0');
        return;
      }
      // customsirge: wrapRect.top runs from >=0 (pin not yet reached) down
      // to -budgetTotal (wrap's bottom now flush with the stage's own
      // bottom -- native `position: sticky`'s own release point, by
      // construction: wrap's height is exactly stageHeight + budgetTotal).
      // No separate "pin start/end" bookkeeping needed -- this one rect
      // read carries the whole scroll-through state.
      // customsirge: 2026-09-28 — the stage sticks at its computed `top`
      // (the header height), so the pin starts when the wrap's top reaches
      // that line, not 0; release is still wrap bottom == stage bottom.
      var pinTop = parseFloat(getComputedStyle(stage).top) || 0;
      var scrolled = pinTop - wrapRect.top;
      if (scrolled < 0) scrolled = 0;
      if (scrolled > budgetTotal) scrolled = budgetTotal;
      var fraction = scrolled / budgetTotal;
      root.style.setProperty('--customsirge-bands-progress', String(fraction * transitions));
    }

    function onResize() {
      fitted = null; // widths/heights changed: re-measure the copy and refit
      onScroll();
    }

    function onScroll() {
      if (disposed || ticking) return;
      ticking = true;
      frame = window.requestAnimationFrame(update);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !listening) {
          listening = true;
          fitted = null; // a resize while off-screen never reached onResize
          window.addEventListener('scroll', onScroll, { passive: true, capture: true });
          window.addEventListener('resize', onResize, { passive: true });
          update();
        } else if (!entry.isIntersecting && listening) {
          listening = false;
          window.removeEventListener('scroll', onScroll, true);
          window.removeEventListener('resize', onResize);
        }
      });
    });
    io.observe(wrap);

    if (desktopQuery && desktopQuery.addEventListener) desktopQuery.addEventListener('change', onResize);
    if (reduceQuery && reduceQuery.addEventListener) reduceQuery.addEventListener('change', onResize);
    window.addEventListener('pageshow', onResize);
    // The copy box depends on the display font's metrics; refit once it has loaded.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(onResize);

    // Re-measure if copy, fonts or images change the mobile card heights.
    var sizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    if (sizeObserver) {
      if (head) sizeObserver.observe(head);
      for (var i = 0; i < cards.length; i++) {
        var copy = cards[i].querySelector('.customsirge-home-benefits-bands__band-content');
        if (copy) sizeObserver.observe(copy);
      }
    }

    // Pause and reduced motion always restore the normal-flow reading layout.
    var pauseObserver = new MutationObserver(onResize);
    pauseObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    update();

    return function cleanup() {
      disposed = true;
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('pageshow', onResize);
      pauseObserver.disconnect();
      if (sizeObserver) sizeObserver.disconnect();
      clearMobile();
      io.disconnect();
      if (listening) {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
      }
      if (desktopQuery && desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onResize);
      if (reduceQuery && reduceQuery.removeEventListener) reduceQuery.removeEventListener('change', onResize);
    };
  }

  Customsirge.register('home_benefits_bands', {
    selector: '[data-customsirge-home-benefits-bands]',
    init: function (root) {
      var teardown = initScrollStack(root);
      return function cleanup() {
        if (typeof teardown === 'function') teardown();
      };
    },
  });
})();
